import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

export const generateArrayFormItemId = (): string => {
  return uuidv4();
};

/**
 * Ensures an existing form (array) object gets a UUID to be used as React key.
 */
export const ensureArrayFormItemId = <T extends { __id?: string }>(
  item: T
): T & { __id: string } => {
  if (item.__id) {
    return item as T & { __id: string };
  }
  return {
    ...item,
    __id: generateArrayFormItemId(),
  };
};

/**
 * Creates a new form (array) object with a UUID to be used as React key.
 */
export const createArrayFormItemWithId = <T>(
  item: Omit<T, '__id' | '_id'>
): T & { __id: string } => {
  return {
    ...item,
    __id: generateArrayFormItemId(),
  } as T & { __id: string };
};

export function useEnsureArrayFormItemIds<T extends { __id?: string }>(
  items: T[] | undefined
): (T & { __id: string })[] {
  const [itemsWithIDs, setItemsWithIDs] = useState<(T & { __id: string })[]>(
    []
  );

  useEffect(() => {
    if (!items || !items.some((item) => !item.__id)) {
      setItemsWithIDs((items as (T & { __id: string })[]) || []);
      return;
    }
    setItemsWithIDs(items.map((item) => ensureArrayFormItemId(item)));
  }, [items]);

  return itemsWithIDs;
}
